import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AttendanceGrid, { AddGuestTile, ReportSubmitBar } from '../../components/AttendanceGrid';
import API from '../../utils/api';
import { ArrowLeftIcon, ClipboardListIcon, UserGroupIcon, AnnotationIcon } from '@heroicons/react/outline';
import {
    getReportByIdRequested,
    getReportsRequested,
    updateAttendance,
    updateGuestAttendance,
    updateTestimonies,
    updateEvaluations,
    updateDate,
    addReportGuest,
    updateReportRequested,
} from "../../actions/reportAction";
import { connect } from "react-redux";
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import moment from 'moment';


const EditReport = ({
    saveStatus,
    saveError,
    user,
    report,
    cellMembers,
    guests,
    updateAttendance,
    updateGuestAttendance,
    updateTestimonies,
    updateEvaluations,
    updateDate,
    addReportGuest: addGuestToReport,
    reportGetRequested,
    getReportsRequested: getReport,
    updateReportRequested: updateReport,
    getReportByIdRequested: getReportById,
}) => {

    const [reportId, setReportId] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [errors, setErrors] = React.useState({});
    const [formIsValid, setFormIsValid] = React.useState(false);
    const [date, setDate] = React.useState("");
    const navigate = useNavigate();
    const { id, cellId } = useParams();

    React.useEffect(() => {
        const isValid = handleValidaton();
        setFormIsValid(isValid);
    }, [report]);

    // Leave the page only after the report and its attendance are saved;
    // on failure stay here so nothing that was entered is lost.
    const submittedRef = React.useRef(false); // only react to a save started on this page
    React.useEffect(() => {
        if (!submittedRef.current) return;
        if (saveStatus === 'saved') {
            submittedRef.current = false;
            notify("Report updated successfully!");
            getReport(cellId);
            navigate('/dashboard/reports');
        } else if (saveStatus === 'failed') {
            submittedRef.current = false;
            toast.error(saveError || 'The report could not be saved. Try again.');
        }
    }, [saveStatus]);

    React.useEffect(() => {
        if (!reportGetRequested) {
            getReportById({ cellId, id });
        }
    }, [reportGetRequested]);

    // Attendance records from the saved report: { id, member_id, attendance, cellMember: { name } }
    const memberPeople = (cellMembers || [])
        .map((m) => ({ id: m.member_id, name: m.cellMember?.name, present: m.attendance === true }))
        .filter((p) => p.id != null);
    const guestPeople = (guests || [])
        .map((g) => ({ id: g.guest_id, name: g.Guest?.name, present: g.attendance === true }))
        .filter((p) => p.id != null);

    const setAllMembers = (value) => {
        memberPeople.forEach((p) => updateAttendance({ id: p.id, value }));
    };
    const setAllGuests = (value) => {
        guestPeople.forEach((p) => updateGuestAttendance({ id: p.id, value }));
    };

    // Saves the visitor as a guest of this cell and adds them to this report as
    // present. With no attendance id yet, the update creates their attendance row.
    const handleAddGuest = async ({ name, phoneNumber }) => {
        const created = await API.postData(`cells/${cellId}/guests`, { name, phoneNumber });
        if (!created || created.id == null) throw new Error('Could not add guest. Try again.');
        addGuestToReport({ guest_id: created.id, attendance: true, Guest: { name: created.name } });
    };

    const handleTmChange = (e) => {
        updateTestimonies(e.target.value);
    }

    const handleEvChange = (e) => {
        updateEvaluations(e.target.value);
    }

    const handleDateChange = (e) => {
        updateDate(e.target.value);
    }

    const notify = (msg) => toast.success(msg, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        submittedRef.current = true;
        updateReport({
            id: report.id,
            cell_id: cellId,
            cellMemberAttendance: cellMembers,
            guestAttendance: guests,
            evaluations: report.evaluations,
            testimonies: report.testimonies,
            date: report.date,
        })
        // Success/failure is handled in the effect above, once the server has answered.

    }

    const handleValidaton = () => {
        let errors = {};
        let formIsValid = true;
        if (report != null && (report.date == null || report.date === "")) {
            formIsValid = false;
            errors["date"] = "Date cannot be empty";
        }

        setErrors(errors);
        return formIsValid;
    }

    // const data = React.useMemo(() => cellMembers, [])

    return (
        <div className="min-h-screen bg-gray-100 text-gray-900">
            {report && (
                <main className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 pt-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => navigate(-1)}
                                aria-label="Back"
                                className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white p-2 text-gray-700 shadow-sm hover:bg-gray-50"
                                type="button"
                            >
                                <ArrowLeftIcon className="h-5 w-5" />
                            </button>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">Edit Report</h1>
                                <p className="text-gray-500 mt-1">Update attendance, testimonies, and evaluations.</p>
                            </div>
                        </div>
                        <div className="bg-white shadow rounded-lg p-3 w-full sm:w-auto">
                            <label htmlFor="report-date" className="block text-sm font-medium text-gray-700">Report Date</label>
                            <input
                                id="report-date"
                                className="mt-2 px-3 py-2 block w-full rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-emerald-600 sm:text-sm sm:leading-6"
                                required
                                name="date"
                                type="date"
                                value={moment(report.date, "YYYY-MM-DD").format("YYYY-MM-DD")}
                                onChange={handleDateChange}
                            />
                            {errors.date && <p className="mt-1 text-xs text-red-600">{errors.date}</p>}
                        </div>
                    </div>

                    <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
                        <AttendanceGrid
                            title="Cell Members"
                            icon={<UserGroupIcon className="h-5 w-5 text-emerald-600" />}
                            people={memberPeople}
                            onToggle={(memberId, value) => updateAttendance({ id: memberId, value })}
                            onSetAll={setAllMembers}
                            emptyText="No member attendance was recorded for this report."
                        />

                        <AttendanceGrid
                            title="Guests"
                            icon={<ClipboardListIcon className="h-5 w-5 text-emerald-600" />}
                            people={guestPeople}
                            onToggle={(guestId, value) => updateGuestAttendance({ id: guestId, value })}
                            onSetAll={guestPeople.length > 1 ? setAllGuests : null}
                            emptyText="No guests on this report. Add a first-time visitor below."
                        >
                            <AddGuestTile onAdd={handleAddGuest} />
                        </AttendanceGrid>

                        <section className="bg-white shadow-lg rounded-lg p-6">
                            <div className="flex items-center gap-2">
                                <AnnotationIcon className="h-5 w-5 text-emerald-600" />
                                <h2 className="text-lg font-semibold text-gray-900">Notes</h2>
                            </div>
                            <div className="mt-6">
                                <label htmlFor="testimonies" className="block text-sm font-medium text-gray-700">Testimonies</label>
                                <textarea
                                    id="testimonies"
                                    name="testimonies"
                                    rows={5}
                                    className="mt-2 px-3 py-2 block w-full rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-emerald-600 sm:text-sm sm:leading-6"
                                    value={report.testimonies || ""}
                                    onChange={handleTmChange}
                                />
                            </div>
                            <div className="mt-6">
                                <label htmlFor="evaluations" className="block text-sm font-medium text-gray-700">Evaluations</label>
                                <p className="text-sm text-gray-500 mt-1">(Comments and prayer requests)</p>
                                <textarea
                                    id="evaluations"
                                    name="evaluations"
                                    rows={5}
                                    className="mt-2 px-3 py-2 block w-full rounded-md border-0 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-emerald-600 sm:text-sm sm:leading-6"
                                    value={report.evaluations || ""}
                                    onChange={handleEvChange}
                                />
                            </div>
                        </section>

                        <ReportSubmitBar
                            membersPresent={memberPeople.filter((p) => p.present).length}
                            membersTotal={memberPeople.length}
                            guestsPresent={guestPeople.filter((p) => p.present).length}
                            label={saveStatus === 'saving' ? 'Saving…' : 'Save Changes'}
                            disabled={!formIsValid || saveStatus === 'saving'}
                            onSubmit={handleSubmit}
                        />
                    </form>
                </main>
            )}
        </div>
    );
};

EditReport.propTypes = {
    report: PropTypes.object,
    cellMembers: PropTypes.array,
    guests: PropTypes.array,
    getReportByIdRequested: PropTypes.func,
    getReportsRequested: PropTypes.func,
    reportGetRequested: PropTypes.bool,
};

EditReport.defaultProps = {
    report: null,
    cellMembers: [],
    guests: [],
    reportGetRequested: false,
};

const mapStateToProps = (state) => ({
    report: state.report.report,
    cellMembers: state.report.cellMembers,
    guests: state.report.guests,
    saveStatus: state.report.saveStatus,
    saveError: state.report.saveError,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
    getReportByIdRequested,
    getReportsRequested,
    updateAttendance,
    updateGuestAttendance,
    updateTestimonies,
    updateEvaluations,
    updateDate,
    addReportGuest,
    updateReportRequested,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(EditReport); 